import { useState, useRef, useEffect } from "react";
import {
    Trash2,
    Plus,
    X,
    Copy,
    ChevronUp,
    ChevronDown,
    Check,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Tooltip } from "@component/tooltip";
import { FormQuestion } from "@type/assessments";
import TextEditor from "@component/rich-editor/v2/text-editor";

interface FormQuestionCardProps {
    question: FormQuestion;
    index: number;
    onUpdate: (questionIndex: number, field: string, value: any) => void;
    onDelete: (questionIndex: number) => void;
    onDuplicate: (questionIndex: number) => void;
    onMoveUp: (questionIndex: number) => void;
    onMoveDown: (questionIndex: number) => void;
    canMoveUp: boolean;
    canMoveDown: boolean;
    disabled?: boolean;
    showPoints?: boolean;
    onAddOption: (questionIndex: number) => void;
    onRemoveOption: (questionIndex: number, optionIndex: number) => void;
    onUpdateOption: (questionIndex: number, optionIndex: number, value: string) => void;
    onUpdateRatingScale: (questionIndex: number, field: string, value: any) => void;
}

export default function FormQuestionCard({
    question,
    index,
    onUpdate,
    onDelete,
    onDuplicate,
    onMoveUp,
    onMoveDown,
    canMoveUp,
    canMoveDown,
    disabled = false,
    showPoints = false,
    onAddOption,
    onRemoveOption,
    onUpdateOption,
    onUpdateRatingScale,
}: FormQuestionCardProps) {
    const [newKeyword, setNewKeyword] = useState("");
    const [isAddingAnswer, setIsAddingAnswer] = useState(false);
    const answerInputRef = useRef<HTMLInputElement>(null);

    // Correct answer keywords synced with question.correct_answers
    const correctAnswerKeywords = question.correct_answers || [];
    // Correct option index synced with question.option_correct_index
    const correctOptionIndex = question.option_correct_index ?? null;

    useEffect(() => {
        if (isAddingAnswer && answerInputRef.current) {
            answerInputRef.current.focus();
        }
    }, [isAddingAnswer]);

    const addKeyword = () => {
        if (newKeyword.trim()) {
            onUpdate(index, "correct_answers", [...correctAnswerKeywords, newKeyword.trim()]);
            setNewKeyword("");
            setIsAddingAnswer(false);
        }
    };

    const removeKeyword = (keywordIndex: number) => {
        onUpdate(index, "correct_answers", correctAnswerKeywords.filter((_, i) => i !== keywordIndex));
    };

    return (
        <div className="bg-white rounded-lg border border-transparent transition-all duration-200 hover:border-blue-500 hover:shadow-md">
            <div className="py-6 px-6">
                {/* Top Bar: Question Number, Type, Actions */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        {/* Question Number */}
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-medium text-sm flex-shrink-0"
                            style={{ border: "1px solid #bfdbfe" }}
                        >
                            {index + 1}
                        </div>

                        {/* Question Type */}
                        <Select
                            value={question.type}
                            onValueChange={(value) => onUpdate(index, "type", value)}
                            disabled={disabled}
                        >
                            <SelectTrigger className="w-40 h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Short Answer">Short Answer</SelectItem>
                                <SelectItem value="Multiple Choice">Multiple Choice</SelectItem>
                                <SelectItem value="Rating Scale">Rating Scale</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Points */}
                        {showPoints && (
                            <div className="flex items-center gap-2">
                                <Label className="text-sm font-medium text-gray-700">Points:</Label>
                                <Input
                                    type="number"
                                    value={question.points || 0}
                                    onChange={(e) => onUpdate(index, "points", parseInt(e.target.value) || 0)}
                                    min="0"
                                    className="w-20 h-9"
                                    disabled={disabled}
                                />
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    {!disabled && (
                        <div className="flex items-center gap-1">
                            <Tooltip content="Duplicate question">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onDuplicate(index)}
                                    className="h-9 w-9 p-0 hover:bg-gray-100"
                                >
                                    <Copy className="h-4 w-4" />
                                </Button>
                            </Tooltip>
                            <Tooltip content="Delete question">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onDelete(index)}
                                    className="h-9 w-9 p-0 hover:bg-gray-100"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </Tooltip>
                            {canMoveUp && (
                                <Tooltip content="Move question up">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onMoveUp(index)}
                                        className="h-9 w-9 p-0 hover:bg-gray-100"
                                    >
                                        <ChevronUp className="h-4 w-4" />
                                    </Button>
                                </Tooltip>
                            )}
                            {canMoveDown && (
                                <Tooltip content="Move question down">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onMoveDown(index)}
                                        className="h-9 w-9 p-0 hover:bg-gray-100"
                                    >
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>

                {/* Question Content */}
                <div className="space-y-4">
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0 border border-gray-200 rounded-lg overflow-hidden">
                                <TextEditor
                                    defaultValue={question.content}
                                    disabled={disabled}
                                    onChange={(value: string) => {
                                        onUpdate(index, "content", value);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Multiple Choice Options */}
                        {question.type === "Multiple Choice" && (
                            <div className="space-y-3">
                                {showPoints && (
                                    <p className="text-xs text-gray-500">
                                        Select the correct answer by clicking the circle next to the option. A tick will appear to mark it as correct.
                                    </p>
                                )}
                                <div className="space-y-2 max-w-2xl">
                                    {(question.options || []).map((option, optionIndex) => (
                                        <div key={optionIndex} className="flex items-center gap-2">
                                            {/* Correct answer selector - only when scoring is enabled */}
                                            {showPoints && (
                                                <Tooltip
                                                    content={correctOptionIndex === optionIndex ? "Correct answer." : "Mark as correct answer"}
                                                >
                                                    <button
                                                        onClick={() => onUpdate(index, "option_correct_index", optionIndex)}
                                                        className={`flex items-center justify-center w-6 h-6 rounded-full border-2 transition-colors flex-shrink-0 ${correctOptionIndex === optionIndex
                                                            ? "bg-teal-500 border-teal-500"
                                                            : "border-gray-300 hover:border-gray-400"
                                                            }`}
                                                    >
                                                        {correctOptionIndex === optionIndex ? (
                                                            <Check className="h-4 w-4 text-white" />
                                                        ) : (
                                                            <span className="text-xs text-gray-400"></span>
                                                        )}
                                                    </button>
                                                </Tooltip>
                                            )}
                                            <div className="relative flex-1">
                                                <Input
                                                    value={option}
                                                    onChange={(e) =>
                                                        onUpdateOption(index, optionIndex, e.target.value)
                                                    }
                                                    placeholder={`Option ${optionIndex + 1}`}
                                                    className="pr-10"
                                                    disabled={disabled}
                                                />
                                                {!disabled && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => onRemoveOption(index, optionIndex)}
                                                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-gray-500 hover:text-red-600 hover:bg-transparent"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {!disabled && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onAddOption(index)}
                                            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Add option
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Short Answer - Correct Answers */}
                        {question.type === "Short Answer" && (
                            <div className="space-y-3">
                                <div className="space-y-1">
                                    <Label className="text-sm font-medium text-gray-700">
                                        Correct answers:
                                    </Label>
                                    <p className="text-xs text-gray-500">
                                        Add the key points or concepts you expect in a strong answer. The AI will use this as guidance when scoring responses.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    {/* Keyword chips */}
                                    <div className="flex flex-wrap gap-2 w-full">
                                        {correctAnswerKeywords.map((keyword, keywordIndex) => (
                                            <div
                                                key={keywordIndex}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 border border-blue-300 rounded-full text-sm"
                                            >
                                                <span className="text-blue-700">{keyword}</span>
                                                <button
                                                    onClick={() => removeKeyword(keywordIndex)}
                                                    className="text-blue-700 hover:text-blue-900"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Input field for adding new answer */}
                                    {isAddingAnswer && (
                                        <div className="relative">
                                            <Input
                                                ref={answerInputRef}
                                                value={newKeyword}
                                                onChange={(e) => setNewKeyword(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        addKeyword();
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (!newKeyword.trim()) {
                                                        setIsAddingAnswer(false);
                                                    }
                                                }}
                                                placeholder="Enter a correct answer"
                                                className="pr-16"
                                            />
                                            <Button
                                                onClick={addKeyword}
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-medium"
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    )}

                                    {/* Add answer button */}
                                    {!isAddingAnswer && !disabled && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setIsAddingAnswer(true)}
                                            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Add answer
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Rating Scale Settings */}
                        {question.type === "Rating Scale" && (
                            <div className="space-y-4">
                                <div className="space-y-1">
                                    <h4 className="text-sm font-medium text-gray-700">
                                        Rating Scale Settings
                                    </h4>
                                    <p className="text-xs text-gray-500">
                                        Configure the scoring scale and define what the lowest and highest ratings mean.
                                    </p>
                                </div>
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm text-gray-600">Minimum Rating</Label>
                                        <Input
                                            type="number"
                                            value={question.min_rate || 1}
                                            onChange={(e) =>
                                                onUpdateRatingScale(index, "min_rate", parseInt(e.target.value) || 1)
                                            }
                                            min="1"
                                            max="10"
                                            placeholder="1"
                                            disabled={disabled}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm text-gray-600">Minimum Label</Label>
                                        <Input
                                            value={question.min_label || ""}
                                            onChange={(e) =>
                                                onUpdateRatingScale(index, "min_label", e.target.value)
                                            }
                                            placeholder="e.g., Low"
                                            disabled={disabled}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm text-gray-600">Maximum Rating</Label>
                                        <Input
                                            type="number"
                                            value={question.max_rate || 5}
                                            onChange={(e) =>
                                                onUpdateRatingScale(index, "max_rate", parseInt(e.target.value) || 5)
                                            }
                                            min="2"
                                            max="10"
                                            placeholder="5"
                                            disabled={disabled}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm text-gray-600">Maximum Label</Label>
                                        <Input
                                            value={question.max_label || ""}
                                            onChange={(e) =>
                                                onUpdateRatingScale(index, "max_label", e.target.value)
                                            }
                                            placeholder="e.g., High"
                                            disabled={disabled}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
